import { Component, OnInit, inject, Output, EventEmitter, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HelpArticleService } from '../../services/help-article.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PermissionService } from '@services/core/permission.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-help-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ListCardComponent, MobileFooterComponent],
  templateUrl: './help-list.component.html',
  styleUrl: './help-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class HelpListComponent implements OnInit {
  articles: any[] = [];
  tempArticles: any[] = [];
  filtered: any[] = [];
  searchText = '';
  isLoading = true;
  isEditMode = false;
  isSaving = false;
  rows = Array.from({ length: 5 }, (_, i) => i + 1);
  selectedId = signal<any>(null);

  public appSettings = inject(AppSettings);
  private helpService = inject(HelpArticleService);
  readonly perm = inject(PermissionService);

  @Output() selectEvent = new EventEmitter<any>();

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true
  });

  ngOnInit(): void {
    this.load();
  }

  get displayRows(): any[] {
    return this.isEditMode ? this.tempArticles : this.articles;
  }

  load(): void {
    this.isLoading = true;
    this.helpService.get().subscribe({
      next: (res) => {
        this.articles = res || [];
        if (this.isEditMode) {
          this.tempArticles = JSON.parse(JSON.stringify(this.articles));
        }
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void {
    const source = this.displayRows;
    const term = (this.searchText || '').trim().toLowerCase();
    this.filtered = source.filter((a) => {
      if (!term) return true;
      return [a.title, a.routePath, a.summary, a.audience]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }

  select(article: any): void {
    if (this.isEditMode) return;
    this.selectedId.set(article.id);
    this.selectEvent.emit(article);
  }

  canQuickEdit(): boolean {
    return this.perm.canCurrent('canUpdate');
  }

  canDelete(): boolean {
    return this.perm.canCurrent('canDelete');
  }

  deleteArticle(article: any): void {
    if (!this.canDelete() || !article?.uuid) return;
    Swal.fire({
      title: '¿Eliminar artículo?',
      text: 'También se eliminarán las capturas asociadas.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar'
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.helpService.delete(article.uuid).subscribe({
        next: () => {
          this.Toast.fire({ icon: 'success', title: 'Eliminado' });
          if (this.selectedId() === article.id) {
            this.selectedId.set(null);
          }
          this.load();
        },
        error: (err) => {
          this.Toast.fire({
            icon: 'error',
            title: err?.error?.error || 'No se pudo eliminar'
          });
        }
      });
    });
  }

  toggleEditMode(): void {
    if (!this.canQuickEdit()) return;
    this.isEditMode = true;
    this.tempArticles = JSON.parse(JSON.stringify(this.articles));
    this.applyFilters();
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.tempArticles = [];
    this.isSaving = false;
    this.applyFilters();
  }

  toggleActive(article: any, event?: Event): void {
    event?.stopPropagation();
    if (!this.isEditMode) return;
    article.isActive = article.isActive === false;
  }

  hasPendingChanges(): boolean {
    if (!this.isEditMode) return false;
    const original = new Map(this.articles.map((a) => [a.uuid, a.isActive !== false]));
    return this.tempArticles.some((a) => (a.isActive !== false) !== original.get(a.uuid));
  }

  saveAll(): void {
    if (!this.isEditMode || this.isSaving) return;
    const original = new Map(this.articles.map((a) => [a.uuid, a.isActive !== false]));
    const items = this.tempArticles
      .filter((a) => a?.uuid && (a.isActive !== false) !== original.get(a.uuid))
      .map((a) => ({ uuid: a.uuid, isActive: a.isActive !== false }));

    if (!items.length) {
      this.cancelEdit();
      return;
    }

    this.isSaving = true;
    Swal.fire({ title: 'Guardando estados...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    this.helpService.bulkUpdateActive(items).subscribe({
      next: (res) => {
        Swal.close();
        this.isSaving = false;
        this.isEditMode = false;
        this.tempArticles = [];
        this.Toast.fire({
          icon: 'success',
          title: `Estados actualizados (${res?.updated ?? items.length})`
        });
        this.load();
      },
      error: (err) => {
        this.isSaving = false;
        Swal.fire({
          icon: 'error',
          title: 'No se pudieron guardar los cambios',
          text: err?.error?.error || err?.message || ''
        });
      }
    });
  }

  routeLabel(path: string): string {
    if (path === '' || path == null) return '(inicio / general)';
    return path;
  }
}
